import { useEffect, useState } from 'react';
import { X, Paperclip } from '@phosphor-icons/react';
import { useRecordingPlayer } from '@/providers/recording-player-context';
import telephonyService from '@/services/api/telephony.service';
import { showToast } from '@/config/toast.config';

const isDirectUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const RecordingPlayerCard = () => {
  const { current, close } = useRecordingPlayer();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const callId = current?.callId ?? null;
  const recordingUrl = current?.recordingUrl ?? null;
  const direct = isDirectUrl(recordingUrl);

  useEffect(() => {
    setBlobUrl(null);
    setError(false);
    setUnavailable(false);
    setLoading(false);
  }, [callId]);

  useEffect(() => {
    if (!current || direct || !callId) return;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setError(false);
    setUnavailable(false);
    (async () => {
      try {
        const blob = await telephonyService.fetchRecordingBlob(callId);
        if (cancelled) return;
        createdUrl = window.URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (err) {
        if (cancelled) return;
        setError(true);
        showToast.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [current, direct, callId]);

  useEffect(() => {
    return () => {
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!current) return null;

  const audioSrc = direct ? recordingUrl : blobUrl;
  const label = current.label || `Apel #${callId}`;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-lg"
      role="region"
      aria-label="Player inregistrare apel"
    >
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Paperclip size={14} className="text-gray-500 shrink-0" />
          <span className="truncate text-sm font-medium text-gray-800" title={label}>
            {label}
          </span>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Inchide player"
          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-3">
        {loading && !audioSrc && (
          <div className="text-xs text-gray-500">Se incarca inregistrarea...</div>
        )}
        {error && !loading && (
          <div className="text-xs text-red-500">Nu s-a putut incarca inregistrarea.</div>
        )}
        {unavailable && (
          <div className="text-xs text-gray-600">
            Inregistrarea nu mai este disponibila. Fisierele audio sunt pastrate de operator aproximativ 1-2 luni.
          </div>
        )}
        {audioSrc && !unavailable && (
          <audio
            controls
            autoPlay
            src={audioSrc}
            preload="auto"
            className="w-full"
            onError={() => setUnavailable(true)}
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (!Number.isFinite(d) || d <= 0) setUnavailable(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default RecordingPlayerCard;
